import {
  Badge,
  TextField,
  Tooltip,
  Typography,
  IconWrapper,
} from "@king-ict/eupisi-ui/components";
import { ReactComponent as CriterionIcon } from "@king-ict/eupisi-ui/components/Icon/Criterion.svg";
import { ReactComponent as FaceManIcon } from "@king-ict/eupisi-ui/components/Icon/FaceMan.svg";
import { ReactComponent as InstitutionIcon } from "@king-ict/eupisi-ui/components/Icon/Institution.svg";
import { useBreakpoint } from "@king-ict/eupisi-ui/hooks";
import { getInstitutionDisplayNameAndTown } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { useTypedSelector } from "../../app/rootReducer";
import styles from "./RequestInstitutionHeaderInfo.module.scss";

interface Props {
  currentStep: number;
  currentSubstep?: number;
}

export default function RequestInstitutionHeaderInfo({
  currentStep,
  currentSubstep,
}: Props) {
  const user = useTypedSelector((state) => state.application.user);
  const request = useTypedSelector((state) => state.request.request);
  const { t } = useTranslation();

  const sizeBreakpoint = useBreakpoint();

  const getChildContent = () => {
    return (
      <>
        <IconWrapper color="body-color">
          <FaceManIcon aria-label={t("accessibility.childIcon")} />
        </IconWrapper>
        <span className={styles.nameTitle}>
          {request && request.child
            ? `${request.child.firstName || ""} ${request.child.lastName || ""}`
            : ""}
        </span>
      </>
    );
  };

  const getInstitutionInfo = () => {
    return (
      <>
        <IconWrapper color="primary">
          <InstitutionIcon aria-label={t("accessibility.institutionIcon")} />
        </IconWrapper>
        <Typography variant="mediumBold" component="h2" color="primary">
          {getInstitutionDisplayNameAndTown(
            request.selectedInstitution.name,
            request.selectedInstitution.town
          )}
        </Typography>
      </>
    );
  };

  const showHeaderInstituionInfo = (): boolean => {
    return (
      request.selectedInstitution !== null &&
      request.selectedInstitution.pedagogicalYearInstitutionId !== null &&
      (currentStep > 2 ||
        (currentStep === 2 &&
          (currentSubstep === 1 ||
            (user.currentPedagogicalYearInstitutionId !== null &&
              currentSubstep === 0))))
    );
  };

  return (
    <Row className={styles.subHeader}>
      {sizeBreakpoint === "xs" || sizeBreakpoint === "sm" ? (
        <Col xs={12}>{getChildContent()}</Col>
      ) : (
        <Col md={3} className={styles.childInfo}>
          <div className={styles.childInfoTitle}>{getChildContent()}</div>
          {showHeaderInstituionInfo() ? (
            <div className={styles.horizontalLine}></div>
          ) : (
            <></>
          )}
        </Col>
      )}

      {showHeaderInstituionInfo() ? (
        <Col
          md={sizeBreakpoint === "xs" || sizeBreakpoint === "sm" ? 12 : 9}
          className={styles.selectedInstitutionInfo}
        >
          <div className={styles.toolTipsWrapper}>
            <Tooltip
              placement={
                sizeBreakpoint === "xs" || sizeBreakpoint === "sm"
                  ? "bottom"
                  : "bottom-start"
              }
              overlay={
                <div className={styles.institutionTooltipInfo}>
                  <Row>
                    <Col xs={12}>
                      <Typography variant="smallHeading" component="h4">
                        {t("request.mainInstitutionInfo")}
                      </Typography>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={12} md={3}>
                      <TextField
                        plaintext
                        bold
                        label={t("common.address")}
                        value={request.selectedInstitution.address}
                      />
                    </Col>
                    <Col sm={12} md={3}>
                      <TextField
                        plaintext
                        bold
                        type="text"
                        label={t("common.phone")}
                        value={request.selectedInstitution.phone}
                      />
                    </Col>
                    <Col sm={12} md={3}>
                      <TextField
                        plaintext
                        bold
                        type="email"
                        label={t("common.email")}
                        value={request.selectedInstitution.email}
                        color="primary"
                      />
                    </Col>
                    <Col sm={12} md={3}>
                      <TextField
                        plaintext
                        bold
                        type="url"
                        label={t("common.webAddress")}
                        value={request.selectedInstitution.webAddress}
                        color="primary"
                      />
                    </Col>
                  </Row>
                  <Row className={styles.rowSpacing}>
                    <Col sm={12} md={3}>
                      <TextField
                        plaintext
                        bold
                        label={t("institutionSearchPage.founderType")}
                        value={request.selectedInstitution.founderType}
                      />
                    </Col>
                    <Col sm={12} md={3}>
                      <TextField
                        plaintext
                        bold
                        type="text"
                        label={t("institutionSearchPage.institutionType")}
                        value={request.selectedInstitution.institutionType}
                      />
                    </Col>
                  </Row>
                </div>
              }
            >
              <div className={styles.infoSection}>{getInstitutionInfo()}</div>
            </Tooltip>
            {sizeBreakpoint !== "xs" &&
            sizeBreakpoint !== "sm" &&
            currentStep === 2 &&
            (currentSubstep === 1 ||
              (user.currentPedagogicalYearInstitutionId &&
                currentSubstep === 0)) ? (
              <Tooltip
                placement="bottom"
                overlay={
                  <div className={styles.institutionCriteriaTooltipInfo}>
                    <Row>
                      <Col xs={12}>
                        <Typography variant="smallHeading" component="h4">
                          {t("request.criteriaAndPoints")}
                        </Typography>
                      </Col>
                    </Row>
                    {request.selectedInstitution.criteria.map(
                      (criterion, index) => {
                        return (
                          <div
                            key={`${request.selectedInstitution.pedagogicalYearInstitutionId}_criterionHeaderTooltip_${index}`}
                            className={styles.criterionToolTipRow}
                          >
                            {" "}
                            {criterion.isDirectEnrollment ? (
                              ""
                            ) : (
                              <Badge
                                value={criterion.points}
                                color="grey"
                              ></Badge>
                            )}
                            <Typography color="light">
                              {criterion.isDirectEnrollment
                                ? "(" +
                                  t("criteriaStep.directEnrollment") +
                                  ") "
                                : ""}
                              {criterion.name}
                            </Typography>
                          </div>
                        );
                      }
                    )}
                  </div>
                }
              >
                <div className={styles.criterionSection}>
                  <IconWrapper color="primary">
                    <CriterionIcon
                      aria-label={t("accessibility.criterionIcon")}
                    />
                  </IconWrapper>
                  <Typography
                    variant="mediumBold"
                    component="h3"
                    color="primary"
                  >
                    {t("request.criteriaAndPoints")}
                  </Typography>
                </div>
              </Tooltip>
            ) : (
              <></>
            )}
          </div>
        </Col>
      ) : (
        <></>
      )}

      {sizeBreakpoint === "xs" || sizeBreakpoint === "sm" ? (
        <Col xs={12}>
          <div className={styles.mobileLastRow}></div>
        </Col>
      ) : (
        <></>
      )}
    </Row>
  );
}
